/**
 * Guest Optimization Storage Utility
 * 
 * Manages localStorage for guest users who optimize resumes before authenticating.
 * Stores optimization data temporarily so it can be restored after login/signup.
 */

interface GuestOptimizationData {
  currentStep: number;
  resumeData: any;
  jobData: any;
  baselineScores: any;
  optimizationResult: any;
  timestamp: number;
}

const STORAGE_KEY = 'guestOptimization';
const SESSION_FALLBACK_KEY = 'guestOptimization_session';
const PENDING_DOWNLOAD_KEY = 'pendingOptimizationDownload';
const EXPIRY_TIME = 60 * 60 * 1000; // 1 hour

export const guestOptimizationStorage = {
  /**
   * Save guest optimization data to localStorage (with sessionStorage fallback on quota)
   */
  save: (data: Omit<GuestOptimizationData, 'timestamp'>): void => {
    const storageData: GuestOptimizationData = {
      ...data,
      timestamp: Date.now(),
    };
    const serialized = JSON.stringify(storageData);
    try {
      localStorage.setItem(STORAGE_KEY, serialized);
      try {
        sessionStorage.setItem(SESSION_FALLBACK_KEY, serialized);
      } catch {
        // sessionStorage can also hit quota; ignore
      }
      console.log('✅ Guest optimization data saved to localStorage');
    } catch (error) {
      // QuotaExceededError or other: try sessionStorage so resume is not lost
      try {
        sessionStorage.setItem(SESSION_FALLBACK_KEY, serialized);
        console.log('✅ Guest optimization data saved to sessionStorage (localStorage full)');
      } catch (sessionErr) {
        console.error('❌ Failed to save guest optimization data:', error);
      }
    }
  },

  /**
   * Get guest optimization data from localStorage (or sessionStorage fallback)
   * Returns null if expired or not found
   */
  get: (): GuestOptimizationData | null => {
    try {
      let stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) stored = sessionStorage.getItem(SESSION_FALLBACK_KEY);
      if (!stored) return null;

      const data: GuestOptimizationData = JSON.parse(stored);

      // Check if data is expired
      if (Date.now() - data.timestamp > EXPIRY_TIME) {
        console.log('⏰ Guest optimization data expired, clearing...');
        guestOptimizationStorage.clear();
        return null;
      }

      return data;
    } catch (error) {
      console.error('❌ Failed to get guest optimization data:', error);
      return null;
    }
  },

  /**
   * Clear guest optimization data from localStorage and sessionStorage
   */
  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SESSION_FALLBACK_KEY);
      localStorage.removeItem(PENDING_DOWNLOAD_KEY);
      console.log('🗑️ Guest optimization data cleared');
    } catch (error) {
      console.error('❌ Failed to clear guest optimization data:', error);
    }
  },

  /**
   * Set pending download flag (user tried to download but needs to auth first)
   */
  setPendingDownload: (): void => {
    try {
      localStorage.setItem(PENDING_DOWNLOAD_KEY, 'true');
    } catch (error) {
      console.error('❌ Failed to set pending download flag:', error);
    }
  },

  /**
   * Check if there's a pending download
   */
  hasPendingDownload: (): boolean => {
    try {
      return localStorage.getItem(PENDING_DOWNLOAD_KEY) === 'true';
    } catch (error) {
      console.error('❌ Failed to check pending download flag:', error);
      return false;
    }
  },

  /**
   * Clear pending download flag
   */
  clearPendingDownload: (): void => {
    try {
      localStorage.removeItem(PENDING_DOWNLOAD_KEY);
    } catch (error) {
      console.error('❌ Failed to clear pending download flag:', error);
    }
  },
};

export default guestOptimizationStorage;
